import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params} from '@angular/router';
import {forkJoin} from 'rxjs';
import {CompetitionService} from '../../../../services/competition/competition.service';
import {
  CompetitionGoalModel,
  CompetitionModel,
  CompetitionParticipantSchoolModel,
  CompetitionParticipantSchoolFilter,
  PublicCompetitionParticipantSchoolFilter
} from '../../../../models/competition/competition.model';
import {
  CompetitionSchoolActivityFilter,
  CompetitionSchoolActivityGroupedModel,
  CompetitionSchoolActivityModel
} from '../../../../models/competition/competition-activity.model';
import {CultureModel} from '../../../../models/localization-and-pricing/culture.model';
import {Enums} from '../../../../models/extensions/enums';
import {AdminIntegrationService} from '../../../../services/admin/admin-integration.service';
import {IntegrationCompetitionAwardModel} from '../../../../models/qrcode-integrations/integration.model';
import {COMPETITION_YEARS} from '../competition-years';
import {ConsumerCompetitionStats} from '../../../../models/competition/consumer-competition-stats.model';
import {NavigationModel} from '../../../../models/localization-and-pricing/navigation.model';
import {RewardGrantTypes} from '../../../../models/qrcode-integrations/integration.model';
import {UserModel} from '../../../../models/authentication/user.model';
import {NumberExtensionModel} from '../../../../models/extensions/number-extension.model';
import {LiveCompetitionCounts} from '../live-competition-counts';
import {AdminCompetitionService} from '../../../../services/admin/admin-competition.service';

export interface WeekTab {
  index: number;
  label: string;
  dates: string;
}

export interface HomeGoalCard {
  goal: CompetitionGoalModel;
  reached: number;
  pct: number;
}

export interface PodiumSlot {
  rank: number;
  school: string;
  score: string;
  height: number;
  color: string;
}

/**
 * Redesigned competition page (v2). The original competition-detail page is
 * kept as is; this one lives on the /detail-v2 route.
 */
@Component({
  selector: 'app-competition-detail-v2',
  templateUrl: './competition-detail-v2.component.html',
  styleUrls: ['./competition-detail-v2.component.css']
})
export class CompetitionDetailV2Component implements OnInit, OnDestroy {

  private static MONTHS = ['jan.', 'feb.', 'mars', 'apríl', 'maí', 'júní', 'júlí', 'ágúst', 'sept.', 'okt.', 'nóv.', 'des.'];
  private static MONTHS_FULL = ['janúar', 'febrúar', 'mars', 'apríl', 'maí', 'júní', 'júlí', 'ágúst',
    'september', 'október', 'nóvember', 'desember'];
  private static DAY = 1000 * 60 * 60 * 24;
  /** Children that must take part at home before the home counts are shown. */
  private static HOME_MIN_STUDENTS = 10;

  static YEARS = COMPETITION_YEARS;

  comp: CompetitionModel;
  /** The reward grant type of each goal's award, by goal id. */
  private grantTypeByGoalId: { [goalId: string]: string } = {};
  /** The redeem deadline of each goal's award, by goal id. */
  private redeemEndAtByGoalId: { [goalId: string]: Date } = {};
  /** The consumer competition behind the home challenge section, loaded once with its stats. */
  homeComp: CompetitionModel = null;
  homeStats: ConsumerCompetitionStats = null;
  homeGoals: HomeGoalCard[] = [];
  previousComp: CompetitionModel;
  previousWinner: CompetitionParticipantSchoolModel;
  country: string;
  culture: string;
  weeks: CompetitionSchoolActivityGroupedModel[] = [];
  topSchools: CompetitionParticipantSchoolModel[] = [];
  selectedWeek = 0;

  /**
   * Which layout the rewards section uses. Change this one value to switch:
   *  'featured' - the next reward as a big card with all rewards in tiles beside it
   *  'grid'     - every reward as a wide card in a grid
   */
  rewardsLayout: 'featured' | 'grid' = 'featured';

  /**
   * Which message band sits under the hero. Change this one value to switch:
   *  'free-access' - free for all primary schools, with a teacher card to get free access
   *  'classic'     - one line asking whether the class is taking part
   */
  ctaBandLayout: 'free-access' | 'classic' = 'free-access';

  /**
   * Which "Taktu þátt" section is used. Change this one value to switch:
   *  'services' - free access plus the help we offer schools
   *  'classic'  - the original teacher / parent / school leader cards
   */
  joinLayout: 'services' | 'classic' = 'services';

  /** Everything about using Evolytes at home in the competition (parents card, "Taka þátt heima"). Only shown when the home challenge loads. */
  showHomeUsage = true;
  /** The offer card in the home challenge section. Off until the offer and its code are decided. */
  showHomeOffer = false;
  /** The special prizes for the top three in the home challenge section. Off until the prizes are decided. */
  showHomeTopPrizes = false;
  /** "Um Þjóðarátak í stærðfræði" button. Hidden until it has a destination. */
  showAboutNationalEffort = false;

  readonly contactEmail = 'evolytes@evolytes.com';
  readonly teacherTrainingUrl = 'https://evolytesehf.pipedrive.com/scheduler/vXM3GyTY/kennsla-a-evolytes-namskerfid';
  readonly studentPresentationUrl = 'https://calendly.com/siggi-evolytes/kidspresentation';
  showBooking = false;

  /**
   * How the hero looks on small screens. Change this one value to switch:
   *  'playful' - the running children stuck to the left edge with the stats beside them
   *  'stacked' - everything in one centered column
   */
  heroMobileLayout: 'playful' | 'stacked' = 'playful';

  /** The same site header as the landing page, fixed on top of the page. */
  showSiteHeader = true;

  showAward = false;
  integrationAward: IntegrationCompetitionAwardModel = null;
  awardFailed = false;

  /** True when shown under the admin route, where the competition is fetched as an admin so non public competitions work. */
  isAdminView = false;

  private liveCounts = new LiveCompetitionCounts(this.compSv);

  constructor(private compSv: CompetitionService,
              private adminCompSv: AdminCompetitionService,
              private adminIntSv: AdminIntegrationService,
              private route: ActivatedRoute) { }

  ngOnInit() {

    this.isAdminView = this.route.snapshot.data.admin === true;
    if (this.isAdminView) {
      // The admin pages already have their own header
      this.showSiteHeader = false;
    }

    this.route.params.subscribe((params: Params) => {
      this.country = params.country;
      this.culture = params.culture;
      this.resetData();
      this.fetchCompetition(params.competitionId);
    });
  }

  ngOnDestroy() {
    this.liveCounts.destroy();
    if (this.comp) {
      this.comp.endCountdown();
    }
  }

  private resetData() {

    this.liveCounts.setCompetition(null);

    if (this.comp) {
      this.comp.endCountdown();
    }

    this.comp = null;
    this.homeComp = null;
    this.homeStats = null;
    this.homeGoals = [];
    this.previousComp = null;
    this.previousWinner = null;
    this.weeks = [];
    this.topSchools = [];
    this.grantTypeByGoalId = {};
    this.redeemEndAtByGoalId = {};
  }

  fetchCompetition(compId: string) {

    if (!compId) {
      return;
    }

    let compObservable = this.compSv.fetchPublicCompetitionById(compId);
    if (this.isAdminView) {
      compObservable = this.adminCompSv.fetchCompetitionById(compId);
    }

    compObservable.subscribe((comp) => {
      this.comp = comp;
      this.selectedWeek = this.currentWeekIndex();
      this.fetchGrantTypes();
      this.fetchData();
      this.fetchPrevious();
      this.fetchHome();
      this.liveCounts.setCompetition(this.comp);
      if (this.comp.isUpcoming()) {
        this.comp.startCountdown(() => {
          this.fetchData();
        });
      }
    });

  }

  /**
   * The home challenge is the consumer competition linked to this competition in COMPETITION_YEARS. It is loaded
   * once and the section stays hidden if there is none or it cannot be loaded, for example while it is not public.
   */
  fetchHome() {

    let homeId: string = null;
    for (const year of CompetitionDetailV2Component.YEARS) {
      if (year.id === this.comp._id && year.homeId) {
        homeId = year.homeId;
      }
    }

    if (!this.showHomeUsage || !homeId) {
      return;
    }

    const compId = this.comp._id;
    let homeObservable = this.compSv.fetchPublicCompetitionById(homeId);
    let statsObservable = this.compSv.fetchPublicConsumerStats(homeId);
    if (this.isAdminView) {
      homeObservable = this.adminCompSv.fetchCompetitionById(homeId);
      statsObservable = this.adminCompSv.fetchConsumerStats(homeId);
    }

    forkJoin([homeObservable, statsObservable]).subscribe(([homeComp, stats]) => {

      if (!this.comp || this.comp._id !== compId) {
        return;
      }

      this.homeStats = stats;
      this.homeGoals = this.buildHomeGoals(homeComp, stats);
      this.homeComp = homeComp;

    }, () => {
      // Stays hidden
    });

  }

  private buildHomeGoals(homeComp: CompetitionModel, stats: ConsumerCompetitionStats): HomeGoalCard[] {

    const cards: HomeGoalCard[] = [];
    for (const goal of homeComp.goals) {

      let reached = 0;
      let pct = 0;
      for (const goalStats of stats.goals) {
        if (goalStats.goalId === goal._id) {
          reached = goalStats.numReached;
          pct = Math.round(stats.getReachedPercentage(goalStats));
        }
      }

      cards.push({goal, reached, pct});
    }

    return cards;
  }

  /**
   * The counts of children taking part at home are only shown once at least 10 children take part and at
   * least one has reached the first goal, so a handful of children cannot be recognised and the numbers do not look empty.
   */
  homeCountersVisible(): boolean {

    if (!this.homeStats || this.homeStats.numStudents < CompetitionDetailV2Component.HOME_MIN_STUDENTS) {
      return false;
    }

    if (this.homeStats.goals.length === 0) {
      return false;
    }

    return this.homeStats.goals[0].numReached >= 1;
  }

  /**
   * Whether the home challenge section and the places that link to it are shown.
   */
  hasHome(): boolean {
    return this.showHomeUsage && this.homeComp !== null;
  }

  homeTitle(): string {

    const name = this.homeComp.getHomepageName();
    if (name) {
      return name;
    }

    return 'Nóvemberáskorunin heima';
  }

  homeLink(page: string): string[] {
    return ['/'].concat(NavigationModel.getHomeURLPaths([page]));
  }

  /**
   * The counter card, which only exists once the competition has started.
   */
  @ViewChild('liveCounter', {static: false}) set liveCounter(element: ElementRef) {
    this.liveCounts.setCounter(element);
  }

  fetchData() {

    if (this.comp.type !== Enums.CompetitionTypes.multiSchool || this.comp.isUpcoming()) {
      return;
    }

    const weekFilter = new CompetitionSchoolActivityFilter();
    if (this.countsCorrect(this.comp)) {
      weekFilter.sortByAvgCorrect = -1;
    } else {
      weekFilter.sortByAvgAnswers = -1;
    }
    weekFilter.sortGroupByStartAt = 1;
    weekFilter.limit = 3;
    let weeksObservable = this.compSv.fetchPublicSchoolActivitiesGroupedWeekly(this.comp._id, weekFilter);
    if (this.isAdminView) {
      weeksObservable = this.adminCompSv.fetchSchoolActivitiesGroupedWeekly(this.comp._id, weekFilter);
    }
    weeksObservable.subscribe((grouped) => {
      this.weeks = grouped;
    });

    if (this.isAdminView) {
      const adminTopFilter = new CompetitionParticipantSchoolFilter();
      if (this.countsCorrect(this.comp)) {
        adminTopFilter.sortByAvgCorrect = -1;
      } else {
        adminTopFilter.sortByAvgAnswers = -1;
      }
      adminTopFilter.limit = 10;
      this.adminCompSv.fetchParticipantSchoolsAsParticipants(this.comp._id, adminTopFilter).subscribe((schools) => {
        this.topSchools = schools;
      });
      return;
    }

    const topFilter = new PublicCompetitionParticipantSchoolFilter();
    if (this.countsCorrect(this.comp)) {
      topFilter.sortByAvgCorrect = -1;
    } else {
      topFilter.sortByAvgAnswers = -1;
    }
    topFilter.limit = 10;
    this.compSv.fetchPublicParticipantSchools(this.comp._id, topFilter).subscribe((schools) => {
      this.topSchools = schools;
    });

  }

  /**
   * Looks up the award of every goal to know whether the rewards are all given or picked.
   */
  fetchGrantTypes() {

    for (const goal of this.comp.goals) {
      if (goal.integrationId) {
        let integrationObservable = this.compSv.fetchPublicIntegrationById(goal.integrationId);
        if (this.isAdminView) {
          integrationObservable = this.adminIntSv.fetchIntegrationById(goal.integrationId);
        }
        integrationObservable.subscribe((integration) => {
          this.grantTypeByGoalId[goal._id] = integration.rewardGrantType;
          this.redeemEndAtByGoalId[goal._id] = integration.redeemEndAt;
        });
      }
    }

  }

  /**
   * Super admins can reveal any reward, reached or not.
   */
  isSuperAdmin(): boolean {

    const user = UserModel.getCurrent();
    return !!user && user.isAdmin === true;
  }

  /**
   * Whether the reward can no longer be redeemed. The award's own date wins, then the
   * competition's redeem date, then two weeks after the competition ended.
   */
  isRedeemExpired(goal: CompetitionGoalModel): boolean {

    const awardEnd = this.redeemEndAtByGoalId[goal._id];
    if (awardEnd) {
      return awardEnd.getTime() < Date.now();
    }

    if (this.comp.redeemEndAt) {
      return this.comp.redeemEndAt.getTime() < Date.now();
    }

    if (this.comp.endAt) {
      return this.comp.endAt.getTime() + 14 * CompetitionDetailV2Component.DAY < Date.now();
    }

    return false;
  }

  /** Goals a super admin has revealed, by goal id. */
  revealedGoalIds: { [goalId: string]: boolean } = {};

  /**
   * A reward still shown as a silhouette.
   */
  isLocked(goal: CompetitionGoalModel): boolean {

    if (this.revealedGoalIds[goal._id]) {
      return false;
    }
    return this.isGoalHidden(goal);
  }

  /**
   * Whether the reward is a silhouette without a super admin revealing it.
   */
  isGoalHidden(goal: CompetitionGoalModel): boolean {
    return !this.isGoalOpen(goal) && !this.isNextGoal(goal);
  }

  revealLabel(goal: CompetitionGoalModel): string {

    if (this.revealedGoalIds[goal._id]) {
      return 'Fela';
    }
    return 'Sýna';
  }

  toggleReveal(goal: CompetitionGoalModel) {
    this.revealedGoalIds[goal._id] = !this.revealedGoalIds[goal._id];
  }

  isPick(goal: CompetitionGoalModel): boolean {
    return this.grantTypeByGoalId[goal._id] === RewardGrantTypes.pickOne;
  }

  /**
   * Last year's totals and winning school, shown on the newest competition.
   */
  fetchPrevious() {

    const newest = CompetitionDetailV2Component.YEARS[0];
    const previous = CompetitionDetailV2Component.YEARS[1];
    const previousComp = {_id: previous.id} as CompetitionModel;
    if (this.comp._id !== newest.id) {
      return;
    }

    this.compSv.fetchPublicCompetitionById(previous.id).subscribe((comp) => {
      this.previousComp = comp;
    });

    const filter = new PublicCompetitionParticipantSchoolFilter();
    if (this.countsCorrect(previousComp)) {
      filter.sortByAvgCorrect = -1;
    } else {
      filter.sortByAvgAnswers = -1;
    }
    filter.limit = 1;
    this.compSv.fetchPublicParticipantSchools(previous.id, filter).subscribe((schools) => {
      if (schools.length > 0) {
        this.previousWinner = schools[0];
      }
    });

  }

  /**
   * "rétt " when the competition's answer counts only include correct answers, otherwise nothing.
   */
  correctWord(comp?: CompetitionModel): string {

    if (!this.countsCorrect(comp)) {
      return '';
    }

    return 'rétt ';
  }

  /**
   * Whether the competition ranks and unlocks goals on correct answers. 2025 and earlier count all answers.
   */
  countsCorrect(comp?: CompetitionModel): boolean {

    let target = comp;
    if (!target) {
      target = this.comp;
    }

    if (!target) {
      return false;
    }

    for (const year of CompetitionDetailV2Component.YEARS) {
      if (year.id === target._id) {
        return year.countsCorrectAnswers;
      }
    }

    return true;
  }

  /**
   * The total the competition counts: correct answers for 2026, all answers before.
   */
  compTotal(comp: CompetitionModel): number {

    if (this.countsCorrect(comp)) {
      return comp.numCorrect;
    }

    return comp.numAnswers;
  }

  compTotalLabel(comp: CompetitionModel): string {
    return this.format(this.compTotal(comp));
  }

  /**
   * A school's total in the metric the competition counts.
   */
  private schoolTotal(school: { numAnswers: number, numCorrect: number }, comp: CompetitionModel): number {

    if (this.countsCorrect(comp)) {
      return school.numCorrect;
    }

    return school.numAnswers;
  }

  years() {
    return CompetitionDetailV2Component.YEARS;
  }

  yearLink(id: string): string[] {

    if (this.isAdminView) {
      return ['/admin', 'competitions', id, 'preview'];
    }

    return ['/home', this.country, this.culture, 'competitions', id, 'detail-v2'];
  }

  previousYear(): number {
    return CompetitionDetailV2Component.YEARS[1].year;
  }

  /**
   * Opens an email asking for free access for the school.
   */
  accessMailto(): string {

    const subject = 'Fá aðgang að Evolytes - ' + this.comp.getHomepageName();
    const body = [
      'Nafn:',
      'Sími:',
      'Skóli:',
      'Árgangar:',
      'Fjöldi nemenda:',
      '',
      'Endilega komið á framfæri spurningum ef einhverjar eru. Jafnframt er alltaf velkomið að heyra hvað gerði ykkur spennt fyrir því að slást í för í Evolytes ævintýrið.',
      '',
      'Ef að aðrir kennarar ætla sér að taka þátt er velkomið að hengja þau við póstinn.'
    ].join('\r\n');

    return 'mailto:' + this.contactEmail + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
  }

  /**
   * There is no booking form for the setup help, so it goes through email.
   */
  setupMailto(): string {

    const subject = 'Aðstoð við uppsetningu á Evolytes';
    const body = [
      'Við þurfum neðangreindar upplýsingar',
      '',
      'Nafn:',
      'Sími:',
      'Skóli:',
      '',
      'Endilega tilgreinið hvenær væri best að hafa samband eða leggið til tíma til þess að mæla okkur mót til þess að aðstoða ykkur við uppsetningu.',
      '',
      'Við elskum líka símhringingar. Það er ávallt velkomið að hringja í okkur í síma 775-3358 eða 776-6462.'
    ].join('\r\n');

    return 'mailto:' + this.contactEmail + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
  }

  scrollTo(id: string) {

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }

  // Formatting

  format(value: number): string {
    return NumberExtensionModel.delimiterSeparatedNumber(value, '.', 0);
  }

  formatGoal(value: number): string {

    if (value >= 1000000) {
      return (value / 1000000).toString().replace('.', ',') + ' millj.';
    }

    return this.format(value);
  }

  dayMonth(date: Date): string {
    return date.getDate() + '. ' + CompetitionDetailV2Component.MONTHS_FULL[date.getMonth()];
  }

  private shortDayMonth(date: Date): string {
    return date.getDate() + '. ' + CompetitionDetailV2Component.MONTHS[date.getMonth()];
  }

  // Goals

  goalName(goal: CompetitionGoalModel): string {
    return goal.translatedName.stringForCulture(CultureModel.getHomepageCulture());
  }

  isGoalOpen(goal: CompetitionGoalModel): boolean {
    return this.compTotal(this.comp) >= goal.endValue;
  }

  goalPercent(goal: CompetitionGoalModel): number {
    return Math.round(goal.getMastery(this.compTotal(this.comp)));
  }

  /**
   * Rewards are not given automatically; the QR code is scanned in the game.
   */
  showGoalRewardQRCode(goal: CompetitionGoalModel) {

    this.showAward = true;
    this.integrationAward = null;
    this.awardFailed = false;

    // The public endpoint, visitors are not logged in so the authenticated one fails for them
    this.compSv.fetchPublicIntegrationById(goal.integrationId).subscribe((int) => {
      this.integrationAward = int as IntegrationCompetitionAwardModel;
    }, () => {
      this.awardFailed = true;
    });
  }

  numOpenGoals(): number {

    let count = 0;
    for (const goal of this.comp.goals) {
      if (this.isGoalOpen(goal)) {
        count += 1;
      }
    }
    return count;
  }

  /**
   * The goal currently being worked on, or the first one before the start.
   */
  nextGoal(): CompetitionGoalModel {

    for (const goal of this.comp.goals) {
      if (!this.isGoalOpen(goal)) {
        return goal;
      }
    }

    return null;
  }

  isNextGoal(goal: CompetitionGoalModel): boolean {

    const next = this.nextGoal();
    if (!next) {
      return false;
    }

    return next._id === goal._id;
  }

  answersToNextGoal(): number {

    const next = this.nextGoal();
    if (!next) {
      return 0;
    }

    return next.endValue - this.compTotal(this.comp);
  }

  nextGoalPercent(): number {

    const next = this.nextGoal();
    if (!next) {
      return 100;
    }

    return Math.round(next.getMastery(this.compTotal(this.comp)));
  }

  // Weeks

  totalWeeks(): number {
    return Math.max(1, Math.ceil(this.comp.getDurationInDays() / 7));
  }

  /**
   * Zero based index of the week we are in. Before the start this is 0 and
   * after the end it is the last week.
   */
  currentWeekIndex(): number {

    const elapsed = Math.floor((Date.now() - this.comp.startAt.getTime()) / (CompetitionDetailV2Component.DAY * 7));
    return Math.min(Math.max(elapsed, 0), this.totalWeeks() - 1);
  }

  weekTabs(): WeekTab[] {

    const tabs: WeekTab[] = [];
    for (let i = 0; i < this.totalWeeks(); i++) {
      const start = new Date(this.comp.startAt.getTime() + i * 7 * CompetitionDetailV2Component.DAY);
      const end = new Date(start.getTime() + 6 * CompetitionDetailV2Component.DAY);
      tabs.push({index: i, label: 'Vika ' + (i + 1), dates: this.shortDayMonth(start) + ' – ' + this.shortDayMonth(end)});
    }
    return tabs;
  }

  weekStartsLabel(): string {

    const start = new Date(this.comp.startAt.getTime() + this.selectedWeek * 7 * CompetitionDetailV2Component.DAY);
    return 'Vika ' + (this.selectedWeek + 1) + ' hefst ' + this.dayMonth(start);
  }

  isWeekStarted(): boolean {
    return !this.comp.isUpcoming() && this.selectedWeek <= this.currentWeekIndex();
  }

  isSelectedWeekCurrent(): boolean {
    return this.comp.isActive() && this.selectedWeek === this.currentWeekIndex();
  }

  podium(): PodiumSlot[] {

    const result: PodiumSlot[] = [];
    if (this.selectedWeek >= this.weeks.length) {
      return result;
    }

    const schools = this.weeks[this.selectedWeek].schools;
    const heights = {1: 170, 2: 128, 3: 100};
    const colors = {1: '#7A4BAE', 2: '#8A5DBE', 3: '#9A6FC8'};

    // Winner in the middle: 2nd, 1st, 3rd
    for (const rank of [2, 1, 3]) {
      if (rank - 1 < schools.length) {
        const school = schools[rank - 1];
        result.push({
          rank,
          school: school.schoolName,
          score: this.averageScore(school).toFixed(0),
          height: heights[rank],
          color: colors[rank]
        });
      }
    }

    return result;
  }

  // Top schools

  schoolScore(school: CompetitionParticipantSchoolModel, comp?: CompetitionModel): number {

    let target = comp;
    if (!target) {
      target = this.comp;
    }

    const total = this.schoolTotal(school, target);
    if (!school.numStudents) {
      return total;
    }

    return total / school.numStudents;
  }

  /**
   * Average per student for a weekly activity, computed here since the backend only sends the average that was sorted on.
   */
  private averageScore(school: CompetitionSchoolActivityModel): number {

    if (!school.numStudents) {
      return 0;
    }

    return this.schoolTotal(school, this.comp) / school.numStudents;
  }

  schoolBarPercent(school: CompetitionParticipantSchoolModel): number {

    if (this.topSchools.length === 0) {
      return 0;
    }

    const best = this.schoolScore(this.topSchools[0]);
    if (best <= 0) {
      return 0;
    }

    return Math.round((this.schoolScore(school) / best) * 100);
  }

}
